export interface ColorGradeConfig {
  /** Multiplies renderer.toneMappingExposure — brightness, applied before tonemapping. */
  exposure: number;
  /** 1 = neutral; >1 punchier, <1 flatter. Applied as a pivot-around-0.5 curve. */
  contrast: number;
  /** 1 = neutral; 0 = grayscale, >1 more saturated. */
  saturation: number;
}

export const DEFAULT_COLOR_GRADE: ColorGradeConfig = {
  exposure: 1.0,
  contrast: 1.0,
  saturation: 1.0,
};

/**
 * A small custom grading pass rather than three's ColorCorrectionShader:
 * that shader's pow/mul/add uniforms are a fine generic tool but don't map
 * cleanly onto "contrast" and "saturation" as independently understandable
 * controls, which is specifically what was asked for. This runs between
 * RenderPass and OutputPass — i.e. on the linear HDR buffer, before
 * tonemapping/color-space conversion — so grading affects the same data
 * the renderer's own tonemapping will see, not already-tonemapped output.
 */
export const ColorGradeShader = {
  name: 'ColorGradeShader',
  uniforms: {
    tDiffuse: { value: null },
    contrast: { value: DEFAULT_COLOR_GRADE.contrast },
    saturation: { value: DEFAULT_COLOR_GRADE.saturation },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float contrast;
    uniform float saturation;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D( tDiffuse, vUv );

      color.rgb = ( color.rgb - 0.5 ) * contrast + 0.5;

      float luma = dot( color.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
      color.rgb = mix( vec3( luma ), color.rgb, saturation );

      gl_FragColor = color;
    }
  `,
};
